import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { resolveClickable } from '../services/resolve-clickable';
import { anchorEl, anchorMissing } from './tasks-help.shared';

/**
 * ?-Hilfe der Unterseite „Nachweise“ (`/tasks/lage/nachweise`, Paket F14, 09.10.2026).
 * Ersetzt `task-compliance-help`: die Compliance-Seite ist seit F10 diese Unterseite, und die
 * alte Tour erklärte Kacheln, Säulendiagramm und Auffälligkeiten-Liste, die es nicht mehr gibt
 * (Abweichung A10: der vertonte Text nannte noch „teilweise erledigte“ Termine).
 *
 * Kernaussage wie bisher: **Quote, Tabelle und Tagesnachweis zählen nur Routinen**; die drei
 * Karten am Seitenende zählen alle Aufgaben (Station `nw-cards`).
 *
 * Was die Schrittliste erklärt:
 *  - **Tagesnachweis (`?day=…`) und Zeitraum schließen sich aus.** Im Tagesnachweis fehlen
 *    Lage-Karte und Tabelle, im Zeitraum der Tagesabschnitt — die Stationen prüfen in `skipIf`
 *    nur die Existenz und schalten NICHT um (Umschalten lädt neu und zieht den Anker unter dem
 *    Popover weg).
 *  - **Die Tour schaltet die Ansicht nicht auf „Nach Tag“**; `nw-view` zeigt den Umschalter.
 *    Legende und Aufklappen gibt es nur in der Ansicht „Routinen“ mit mindestens einer Zeile.
 *    Steht die Seite beim Start auf „Nach Tag“, schaltet `nw-routines` auf „Routinen“ zurück
 *    (sonst beschrieb die Station eine Tabelle, die nicht da ist, und 5–6 fielen weg).
 *  - **`nw-detail` klappt die erste Routine selbst auf**, falls keine offen ist — der Anker
 *    sitzt auf der Detailzeile, die erst danach im DOM steht. `skipIf` läuft VOR
 *    `onBeforeShow`, prüft deshalb nur „gibt es Zeilen“ (die Legende steht genau dann).
 *  - Export hängt an `canExport`.
 *
 * `readyAnchor`: Start über das ? bleibt auf der Seite — ohne ihn navigierte der Coordinator
 * neu und verlöre `?day=` und die Filter aus der Adresse.
 */
const page = { placement: 'bottom' as const, nextCondition: 'manual' as const, showNextButton: true };

function settle(ms = 300): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Schaltet auf die Ansicht „Routinen“ zurück, falls „Nach Tag“ gewählt ist. Die Stationen
 * `nw-routines` bis `nw-detail` beschreiben die Tabelle; in „Nach Tag“ stünde dort eine Liste
 * von Tagen, und Legende und Aufklappen fielen weg. Reiner Signalwechsel, lädt nichts neu.
 */
export async function ensureRoutinesView(): Promise<void> {
  if (typeof document === 'undefined' || document.querySelector('[data-testid="nachweise-table"]')) return;
  const first = document.querySelector<HTMLElement>('[data-testid="nachweise-view"] [role="radio"]');
  if (!first || first.getAttribute('aria-checked') === 'true') return;
  first.click();
  await settle();
}

/** Klappt die erste Routinenzeile auf, wenn noch keine offen ist. Idempotent, wirft nie. */
export async function ensureFirstRoutineOpen(): Promise<void> {
  if (typeof document === 'undefined' || !anchorMissing(AnchorId.TasksNachweiseDetail)) return;
  const row = document.querySelector<HTMLElement>('[data-testid="nachweise-routine-row"]');
  const toggle = resolveClickable(row?.querySelector<HTMLElement>('xds-icon-button') ?? null);
  if (!toggle) return;
  toggle.click();
  await settle();
}

export const TASK_NACHWEISE_HELP_TOUR: TourConfig = {
  id: TourFeature.TaskNachweiseHelp,
  version: 1,
  title: 'TASK_NACHWEISE_HELP_TOUR_TITLE',
  description: 'TASK_NACHWEISE_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  launchContext: {
    route: '/tasks/lage/nachweise',
    readyAnchor: AnchorId.TasksCompliancePage,
    mode: 'auto',
  },
  triggerContext: { route: '/tasks/lage/nachweise' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 150,
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASK_NACHWEISE_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'nw-welcome',
          anchor: AnchorId.TasksNachweiseHead,
          spotlightAnchor: AnchorId.TasksNachweiseHead,
          title: 'TUTORIAL.TASK_NACHWEISE.WELCOME.TITLE',
          content: 'TUTORIAL.TASK_NACHWEISE.WELCOME.CONTENT',
          ...page,
          align: 'start',
          showPrevButton: false,
          // Schon hier, damit die Nummerierung („1 / 9“) ab der ersten Karte stimmt.
          onBeforeShow: () => ensureRoutinesView(),
        },
        {
          id: 'nw-bar',
          anchor: AnchorId.TasksNachweiseBar,
          spotlightAnchor: AnchorId.TasksNachweiseBar,
          title: 'TUTORIAL.TASK_NACHWEISE.BAR.TITLE',
          content: 'TUTORIAL.TASK_NACHWEISE.BAR.CONTENT',
          ...page,
        },
        {
          // Nur im Zeitraum und mit geladenem Bericht. Der Kernsatz steht nicht immer da.
          id: 'nw-summary',
          anchor: AnchorId.TasksComplianceStats,
          spotlightAnchor: AnchorId.TasksComplianceStats,
          title: 'TUTORIAL.TASK_NACHWEISE.SUMMARY.TITLE',
          content: 'TUTORIAL.TASK_NACHWEISE.SUMMARY.CONTENT',
          ...page,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksComplianceStats),
        },
        {
          id: 'nw-routines',
          anchor: AnchorId.TasksComplianceTrend,
          spotlightAnchor: AnchorId.TasksComplianceTrend,
          title: 'TUTORIAL.TASK_NACHWEISE.ROUTINES.TITLE',
          content: 'TUTORIAL.TASK_NACHWEISE.ROUTINES.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          onBeforeShow: () => ensureRoutinesView(),
          skipIf: () => anchorMissing(AnchorId.TasksComplianceTrend),
        },
        {
          id: 'nw-legend',
          anchor: AnchorId.TasksNachweiseLegend,
          spotlightAnchor: AnchorId.TasksNachweiseLegend,
          title: 'TUTORIAL.TASK_NACHWEISE.LEGEND.TITLE',
          content: 'TUTORIAL.TASK_NACHWEISE.LEGEND.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksNachweiseLegend),
        },
        {
          id: 'nw-detail',
          anchor: AnchorId.TasksNachweiseDetail,
          spotlightAnchor: AnchorId.TasksNachweiseDetail,
          title: 'TUTORIAL.TASK_NACHWEISE.DETAIL.TITLE',
          content: 'TUTORIAL.TASK_NACHWEISE.DETAIL.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          onBeforeShow: () => ensureFirstRoutineOpen(),
          skipIf: () => anchorMissing(AnchorId.TasksNachweiseLegend),
        },
        {
          id: 'nw-view',
          anchor: AnchorId.TasksNachweiseView,
          spotlightAnchor: AnchorId.TasksNachweiseView,
          title: 'TUTORIAL.TASK_NACHWEISE.VIEW.TITLE',
          content: 'TUTORIAL.TASK_NACHWEISE.VIEW.CONTENT',
          ...page,
          align: 'end',
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksNachweiseView),
        },
        {
          id: 'nw-export',
          anchor: AnchorId.TasksComplianceExport,
          spotlightAnchor: AnchorId.TasksComplianceExport,
          title: 'TUTORIAL.TASK_NACHWEISE.EXPORT.TITLE',
          content: 'TUTORIAL.TASK_NACHWEISE.EXPORT.CONTENT',
          ...page,
          align: 'end',
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksComplianceExport),
        },
        {
          // Nur im Tagesnachweis (`?day=…`).
          id: 'nw-day',
          anchor: AnchorId.TasksComplianceDay,
          spotlightAnchor: AnchorId.TasksComplianceDay,
          title: 'TUTORIAL.TASK_NACHWEISE.DAY.TITLE',
          content: 'TUTORIAL.TASK_NACHWEISE.DAY.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksComplianceDay),
        },
        {
          // Die drei Karten über ALLE Aufgaben blenden sich bei fehlendem Recht (403) still aus,
          // der Host bleibt leer im DOM — daher die Höhe.
          id: 'nw-cards',
          anchor: AnchorId.TasksComplianceWorkload,
          spotlightAnchor: AnchorId.TasksComplianceWorkload,
          additionalSpotlightAnchors: [AnchorId.TasksComplianceLeadTime, AnchorId.TasksComplianceActuals],
          title: 'TUTORIAL.TASK_NACHWEISE.CARDS.TITLE',
          content: 'TUTORIAL.TASK_NACHWEISE.CARDS.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          skipIf: () => {
            const el = anchorEl(AnchorId.TasksComplianceWorkload);
            return !el || el.getBoundingClientRect().height < 8;
          },
        },
      ],
    },
  ],
};
